<script lang="ts" setup>
import { ProjectNonWorkingDay } from '@beeboard/common';
import { useProjectStore } from '@/store/projectStore';
import { formatDate, formatDateNumeric } from '@/utils/helpers/various';

/**
 * Editor for a project's custom non-working days (WI #5651) — the shutdown week,
 * the bridge day, the works meeting — shown next to the country/state/region
 * holiday calendar picker it complements.
 *
 * Edit-only by design: it lives in `EditProject.vue`, so a project id always
 * exists and every change is written through immediately. There is no
 * client-side buffering and no create-flow rollback story.
 *
 * The feature is purely visual — these entries only ever drive the map's
 * shading. Nothing here touches a date, a duration or a position.
 */
interface Props {
  projectId: string;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
});

interface Draft {
  id: string | null;
  start_date: string | null;
  end_date: string | null;
  label: string;
}

const projectStore = useProjectStore();
const message = useAlerts();

const saving = ref(false);
const draft = ref<Draft | null>(null);
/**
 * Whether the user has tried to save the current draft. Validation runs from the
 * first keystroke, but opening a blank draft should not greet them with red text
 * for fields they have not reached yet — so the messages stay hidden until the
 * first save attempt.
 */
const submitAttempted = ref(false);

// `projectStore.project()` throws when the project isn't in the store yet, and
// this renders before `EditProject`'s `onMounted` has loaded it (some entry
// points open the edit modal from a list backed by `projectsDetails`, which
// never populates `projects`). Guard with `hasProject` the same way
// `EditProject.vue` does for its own `project` computed.
const nonWorkingDays = computed<ProjectNonWorkingDay[]>(() =>
  projectStore.hasProject(props.projectId)
    ? (projectStore.project(props.projectId).non_working_days ?? [])
    : [],
);

const isBusy = computed(() => props.disabled || saving.value);

/**
 * AC2, form half: the same rule the API enforces. An end before the start is
 * blocked here so the user sees it before the round-trip; the API still rejects
 * it with a 400 for any other client.
 */
const draftErrors = computed<string[]>(() => {
  if (!draft.value) {
    return [];
  }
  const errors: string[] = [];
  if (!draft.value.start_date || !draft.value.end_date) {
    errors.push($t('project.nonWorkingDays.errorDatesRequired'));
  } else if (draft.value.end_date < draft.value.start_date) {
    errors.push($t('project.nonWorkingDays.errorEndBeforeStart'));
  }
  if (!draft.value.label.trim()) {
    errors.push($t('project.nonWorkingDays.errorLabelRequired'));
  }

  return errors;
});

/** The subset of `draftErrors` the user is actually shown — see `submitAttempted`. */
const visibleErrors = computed<string[]>(() =>
  submitAttempted.value ? draftErrors.value : [],
);

function startAdd() {
  draft.value = { id: null, start_date: null, end_date: null, label: '' };
  submitAttempted.value = false;
}

function startEdit(nonWorkingDay: ProjectNonWorkingDay) {
  draft.value = {
    id: nonWorkingDay.id,
    start_date: nonWorkingDay.start_date,
    end_date: nonWorkingDay.end_date,
    label: nonWorkingDay.label,
  };
  submitAttempted.value = false;
}

function cancelDraft() {
  draft.value = null;
  submitAttempted.value = false;
}

/**
 * Convenience only: picking a start date for a new entry pre-fills the end with
 * the same day, so the common case (a single day off) is one click instead of
 * two. Never overwrites an end the user already chose.
 */
function onStartDateChange() {
  if (draft.value && !draft.value.end_date) {
    draft.value.end_date = draft.value.start_date;
  }
}

async function submitDraft() {
  if (!draft.value || isBusy.value) {
    return;
  }
  // AC2, form half: still blocks the submit, but now explains why on demand
  // rather than pre-emptively. The API rejects an inverted range regardless.
  if (draftErrors.value.length > 0) {
    submitAttempted.value = true;

    return;
  }
  const payload = {
    start_date: draft.value.start_date as string,
    end_date: draft.value.end_date as string,
    label: draft.value.label.trim(),
  };
  const editedId = draft.value.id;

  saving.value = true;
  try {
    if (editedId) {
      await projectStore.updateNonWorkingDay(
        props.projectId,
        editedId,
        payload,
      );
    } else {
      await projectStore.createNonWorkingDay(props.projectId, payload);
    }
    draft.value = null;
    submitAttempted.value = false;
    message.success();
  } catch (error) {
    message.error(error);
  } finally {
    saving.value = false;
  }
}

async function remove(nonWorkingDay: ProjectNonWorkingDay) {
  saving.value = true;
  try {
    await projectStore.deleteNonWorkingDay(props.projectId, nonWorkingDay.id);
    if (draft.value?.id === nonWorkingDay.id) {
      draft.value = null;
    }
    message.success();
  } catch (error) {
    message.error(error);
  } finally {
    saving.value = false;
  }
}

/**
 * `formatDateNumeric` carries `timeZone: 'utc'`, which is what makes this safe
 * for these values: `start_date` / `end_date` are calendar dates, so
 * `new Date('2026-12-24')` is midnight UTC and a local-timezone render would
 * show the 23rd anywhere west of UTC.
 */
function formatRange(nonWorkingDay: ProjectNonWorkingDay): string {
  const start = formatDate(nonWorkingDay.start_date, formatDateNumeric);
  if (nonWorkingDay.start_date === nonWorkingDay.end_date) {
    return start;
  }

  return `${start} – ${formatDate(nonWorkingDay.end_date, formatDateNumeric)}`;
}
</script>

<template>
  <UiCollapsible
    class="non-working-days"
    :title="$t('project.nonWorkingDays.title')"
    :is-open="false"
  >
    <template v-if="nonWorkingDays.length" #actions>
      <span class="non-working-days__count">{{ nonWorkingDays.length }}</span>
    </template>

    <ul v-if="nonWorkingDays.length" class="non-working-days__list">
      <li
        v-for="nonWorkingDay in nonWorkingDays"
        :key="nonWorkingDay.id"
        class="non-working-days__item"
      >
        <span class="non-working-days__range">
          {{ formatRange(nonWorkingDay) }}
        </span>
        <span class="non-working-days__label" :title="nonWorkingDay.label">
          {{ nonWorkingDay.label }}
        </span>
        <div class="non-working-days__item-actions">
          <UiButton
            variant="flat"
            size="small"
            :disabled="isBusy"
            :aria-label="$t('button.edit')"
            @click="startEdit(nonWorkingDay)"
          >
            <UiIcon name="Pencil" :size="16" />
          </UiButton>
          <UiButton
            variant="flat"
            size="small"
            :disabled="isBusy"
            :aria-label="$t('button.delete')"
            @click="remove(nonWorkingDay)"
          >
            <UiIcon name="Trash2" :size="16" />
          </UiButton>
        </div>
      </li>
    </ul>

    <div v-if="draft" class="non-working-days__draft">
      <UiField :label="$t('project.nonWorkingDays.startDate')">
        <UIDatePicker
          id="non-working-day-start"
          v-model="draft.start_date"
          compact-view
          :disabled="isBusy"
          @update:model-value="onStartDateChange"
        />
      </UiField>
      <UiField :label="$t('project.nonWorkingDays.endDate')">
        <UIDatePicker
          id="non-working-day-end"
          v-model="draft.end_date"
          compact-view
          :min-date="draft.start_date ?? undefined"
          :disabled="isBusy"
        />
      </UiField>
      <UiField :label="$t('project.nonWorkingDays.label')">
        <UiInput
          id="non-working-day-label"
          v-model="draft.label"
          :disabled="isBusy"
          :maxlength="255"
          :placeholder="$t('project.nonWorkingDays.labelPlaceholder')"
          @keyup.enter="submitDraft"
        />
      </UiField>
      <div class="non-working-days__draft-actions">
        <UiButton
          variant="primary"
          size="small"
          :loading="saving"
          :aria-label="$t('button.save')"
          @click="submitDraft"
        >
          <UiIcon name="Check" :size="16" />
        </UiButton>
        <UiButton
          variant="flat"
          size="small"
          :disabled="saving"
          :aria-label="$t('button.cancel')"
          @click="cancelDraft"
        >
          <UiIcon name="XIcon" :size="16" />
        </UiButton>
      </div>
    </div>

    <p
      v-for="error in visibleErrors"
      :key="error"
      class="non-working-days__error"
    >
      {{ error }}
    </p>

    <UiButton
      v-if="!draft"
      variant="primary-text"
      size="small"
      class="non-working-days__add"
      :disabled="isBusy"
      @click="startAdd"
    >
      <UiIcon name="Plus" :size="16" />
      {{ $t('project.nonWorkingDays.add') }}
    </UiButton>
  </UiCollapsible>
</template>

<style lang="scss" scoped>
.non-working-days {
  // No vertical padding of its own: collapsed, the section must cost exactly
  // one header row, so that adding it to the edit modal does not push the modal
  // into a scrollbar on an ordinary screen.
  display: flex;
  flex-direction: column;
  gap: var(--space-8);

  &__count {
    min-width: px(20);
    padding: 0 var(--space-6);
    border-radius: px(10);
    background: var(--color-gray-100);
    color: var(--color-gray-600);
    font-size: var(--font-size-12);
    font-variant-numeric: tabular-nums;
    text-align: center;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: var(--space-12);
    padding: var(--space-6) var(--space-8);
    border: 1px solid var(--color-gray-200);
    border-radius: var(--border-radius-m);
  }

  &__range {
    flex: 0 0 auto;
    font-variant-numeric: tabular-nums;
    color: var(--color-gray-900);
  }

  &__label {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--color-gray-600);
  }

  &__item-actions {
    display: flex;
    flex: 0 0 auto;
    gap: var(--space-4);
  }

  // One row: both dates, the reason and the confirm/cancel buttons. The buttons
  // sit in the row rather than on their own line below it, which is what used to
  // make the draft box twice as tall as the fields it held.
  &__draft {
    display: grid;
    grid-template-columns: 1fr 1fr 2fr auto;
    align-items: end;
    gap: var(--space-8);
  }

  &__draft-actions {
    display: flex;
    gap: var(--space-4);
  }

  &__error {
    margin: 0;
    font-size: var(--font-size-12);
    color: var(--color-error-600);
  }

  &__add {
    align-self: flex-start;
  }
}
</style>
